import React, { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { CardLens, LensCardData, LensCustomView, LensFilterValues } from '~/types/leLens';

import CustomLensView from './CustomLensView';
import { CUSTOM_VIEW_STARTER } from './starter';

type Props = {
  open: boolean;
  lens: CardLens;
  cards: Array<LensCardData>;
  filters: LensFilterValues;
  period: { from?: string; to?: string; summary: string };
  saving: boolean;
  onClose: () => void;
  onSave: (view: LensCustomView) => Promise<void>;
  onRemove: () => Promise<void>;
};

export default function CustomLensViewDialog({
  open,
  lens,
  cards,
  filters,
  period,
  saving,
  onClose,
  onSave,
  onRemove,
}: Props) {
  const [draft, setDraft] = useState<LensCustomView>(lens.spec.customView ?? CUSTOM_VIEW_STARTER);
  const [surface, setSurface] = useState<'preview' | 'html' | 'css' | 'javascript'>('preview');

  useEffect(() => {
    if (open) {
      setDraft(lens.spec.customView ?? CUSTOM_VIEW_STARTER);
      setSurface('preview');
    }
  }, [lens.spec.customView, open]);

  const field = surface === 'preview' ? null : surface;
  return (
    <Dialog fullWidth maxWidth="xl" onClose={saving ? undefined : onClose} open={open}>
      <DialogTitle>Custom HTML view</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Alert severity="info">
            HTML controls the presentation; Lens still controls the queries, access, period, filters and refresh.
            JavaScript can read
            <code> Lens.ready(payload)</code>, <code>Lens.card(id)</code> and <code>Lens.state()</code>, but cannot use
            the network or access Admin.
          </Alert>
          <Stack
            alignItems={{ sm: 'center', xs: 'stretch' }}
            direction={{ sm: 'row', xs: 'column' }}
            justifyContent="space-between"
            gap={2}
          >
            <ToggleButtonGroup
              exclusive
              onChange={(_event, value) => value && setSurface(value)}
              size="small"
              value={surface}
            >
              <ToggleButton value="preview">Preview</ToggleButton>
              <ToggleButton value="html">HTML</ToggleButton>
              <ToggleButton value="css">CSS</ToggleButton>
              <ToggleButton value="javascript">JavaScript</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              inputProps={{ max: 2400, min: 320, step: 20 }}
              label="View height"
              onChange={(event) => setDraft((current) => ({ ...current, height: Number(event.target.value) }))}
              size="small"
              sx={{ width: 150 }}
              type="number"
              value={draft.height ?? 760}
            />
          </Stack>
          <Divider />
          {surface === 'preview' ? (
            <CustomLensView cards={cards} filters={filters} lens={lens} period={period} view={draft} />
          ) : (
            <Stack spacing={1}>
              <Typography color="text.secondary" variant="caption">
                {surface === 'html' &&
                  'Write the page body. Do not paste live figures here; bind them from the payload in JavaScript.'}
                {surface === 'css' && 'Styles are scoped to the isolated preview document.'}
                {surface === 'javascript' &&
                  'Runs only inside the locked-down preview. Lens.ready runs again whenever live results change.'}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                inputProps={{ spellCheck: false }}
                minRows={22}
                multiline
                onChange={(event) => setDraft((current) => ({ ...current, [field!]: event.target.value }))}
                value={draft[field!] ?? ''}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button color="error" disabled={saving || !lens.spec.customView} onClick={() => void onRemove()}>
          Remove custom view
        </Button>
        <Stack direction="row" spacing={1}>
          <Button disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving || !draft.html.trim()} onClick={() => void onSave(draft)} variant="contained">
            {saving ? 'Saving…' : 'Save custom view'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
